<template>
    <div>
        <div class="d-flex">
            <v-alert type="info" text dense>Changes will take effect after the application is restarted.</v-alert>
        </div>
        <v-skeleton-loader class="mx-auto" type="table" v-if="fetchingMountedStorageList"></v-skeleton-loader>
        <template v-else>
            <div class="d-flex align-center">
                <space-attach-external-storage-dialog
                    v-if="canMountStorage"
                    :space-id="resolvedSpaceId"
                    :dialog-max-width="dialogMaxWidth"
                    @attached="onAttached">
                    <template v-slot:activator="{ on, attrs, open }">
                        <v-btn color="primary" class="mr-2" v-bind="attrs" v-on="on" @click="open">
                            <v-icon left>mdi-connection</v-icon>
                            <span>Storage</span>
                        </v-btn>
                    </template>
                </space-attach-external-storage-dialog>
                <v-spacer></v-spacer>
                <v-text-field
                    placeholder="Search for an attached storage..."
                    autofocus
                    v-model="search"
                    clearable
                    hide-details
                    prepend-inner-icon="mdi-magnify "
                    solo
                    flat
                    dense
                    class="mr-2" />
                <v-btn :loading="fetchingMountedStorageList" @click="fetchMountedStorageList" icon><v-icon>refresh</v-icon></v-btn>
            </div>
            <v-data-table
                :items="mountedStorageList"
                item-key="amid"
                :headers="headers"
                :search="search"
                :options="tableOptions"
                :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
                class="mt-4">
                <template v-slot:top="{ pagination, options, updateOptions }">
                    <v-data-footer
                        :pagination="pagination"
                        :options="options"
                        @update:options="updateOptions"
                        :itemsPerPageOptions="itemsPerPageOptions"
                        items-per-page-text="$vuetify.dataTable.itemsPerPageText"
                        class="no-border" />
                </template>
                <template v-slot:no-data>
                    <span>No additional storage has been attached yet. Click attach to attach a new storage.</span>
                </template>
                <template v-slot:[`item.long_id`]="{ item }">
                    <span class="text-body-1">{{ item.long_id }}</span>
                </template>
                <template v-slot:[`item.provider`]>
                    <img v-if="isSwitchCloudEnabled" src="@/assets/switch-favicon.svg" alt="Switch" class="provider-logo" />
                    <span v-else>-</span>
                </template>
                <template v-slot:[`item.quota_gib`]="{ item }">{{ item.quota_gib }} GiB</template>
                <template v-slot:[`item.read_only`]="{ item }">
                    {{ item.read_only ? 'Read only' : 'Read/Write' }}
                </template>
                <template v-slot:[`item.mount_path`]="{ item }">
                    <code class="text-body-2">{{ item.mount_path }}</code>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <v-menu left offset-x nudge-top="10">
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" text color="secondary" small>
                                <v-icon>more_horiz</v-icon>
                            </v-btn>
                        </template>
                        <v-list nav dense>
                            <copy-to-clipboard :textToCopy="item.mount_path" buttonName="Copy path to clipboard" type="listItem" />
                            <v-list-item v-if="canMountStorage" @click="openEditMountDialog(item)">
                                <v-list-item-icon>
                                    <v-progress-circular v-if="editingMount === item.amid" size="18" width="2" indeterminate color="secondary" />
                                    <v-icon v-else color="secondary">mdi-pencil-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title class="secondary--text mr-5">Edit</v-list-item-title>
                            </v-list-item>
                            <v-divider v-if="canMountStorage" class="my-2"></v-divider>
                            <v-list-item v-if="canMountStorage" @click="openDeleteDialog(item)">
                                <v-list-item-icon>
                                    <v-icon color="error">mdi-trash-can-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title class="error--text mr-5">Remove</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </template>
            </v-data-table>
        </template>

        <v-dialog v-model="editMountDialog" :max-width="dialogMaxWidth" lazy-validation persistent>
            <v-card>
                <v-card-title class="flex-nowrap align-start">
                    <span>Edit attached storage</span>

                    <v-spacer></v-spacer>
                    <v-btn @click="editMountDialog = false" icon><v-icon>clear</v-icon></v-btn>
                </v-card-title>
                <v-card-text class="mb-0">
                    <v-form v-model="mountEditFormIsValid" ref="mountEditForm">
                        <v-text-field v-model="mountPoint" outlined dense :rules="[rules.required, rules.validChars, rules.unique]">
                            <template v-slot:label>Path</template>
                        </v-text-field>
                        <div class="mb-1">Storage will be accessible under</div>
                        <code class="d-flex rounded py-2 px-3">
                            /
                            <b>{{ mountPoint }}</b>
                        </code>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>

                    <v-btn text color="primary" @click="sendPatchRequest" :disabled="!mountEditFormIsValid" :loading="editingMount === selectedMount?.amid">
                        Edit
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="deleteMountDialog" max-width="500" persistent>
            <v-card flat>
                <v-card-title class="flex-nowrap align-start">
                    <div class="d-flex align-center secondary--text">
                        <span>Confirm detach</span>
                    </div>

                    <v-spacer></v-spacer>
                    <v-btn @click="deleteMountDialog = false" icon><v-icon>clear</v-icon></v-btn>
                </v-card-title>
                <v-card-text>Are you sure you would like to detach this storage?</v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text @click="confirmDeleteMount">Detach</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'

const CopyToClipboard = () => import('@/components/CopyToClipboard.vue')
const SpaceAttachExternalStorageDialog = () => import('@/modules/space/components/SpaceAttachExternalStorageDialog.vue')

export default {
    name: 'SpaceExternalStorage',
    props: {
        spaceId: {
            type: [String, Number],
            default: null
        },
        isSpaceAdminOverride: {
            type: Boolean,
            default: null
        }
    },
    components: { CopyToClipboard, SpaceAttachExternalStorageDialog },
    data() {
        return {
            fetchingMountedStorageList: false,
            headers: [
                { text: 'Name', value: 'long_id', align: 'left' },
                { text: 'Provider', value: 'provider', align: 'left', sortable: false },
                { text: 'Access', value: 'read_only', align: 'left' },
                { text: 'Quota', value: 'quota_gib', align: 'left' },
                { text: 'Path', value: 'mount_path', align: 'right' },
                { text: 'Actions', value: 'actions', align: 'right', sortable: false }
            ],
            itemsPerPageOptions: [25, 50, 100, -1],
            tableOptions: {
                page: 0,
                itemsPerPage: 25,
                pageStart: 1,
                pageStop: 25,
                itemsLength: 25
            },
            mountedStorageList: [
                {
                    amid: 1001,
                    long_id: 'switchdemo',
                    read_only: false,
                    quota_gib: 5000,
                    mount_path: '/data'
                }
            ],
            rules: {
                required: value => !!value || 'This field is required',
                validChars: p => /^[a-zA-Z0-9-_]*$/g.test(p) || 'Please use only letters, numbers, underscores and dashes',
                unique: value => !this.mountPoints.includes(value) || 'A storage is already mounted on that path'
            },
            search: '',
            selectedMount: null,
            editMountDialog: false,
            mountEditFormIsValid: true,
            mountPoint: '',
            deleteMountDialog: false,
            deletingMount: false,
            editingMount: null,
            dialogMaxWidth: '640px'
        }
    },
    computed: {
        ...mapGetters('spaceStore', ['isSpaceAdmin']),
        resolvedSpaceId() {
            return this.spaceId || this.$route.params.sid
        },
        canMountStorage() {
            if (this.isSpaceAdminOverride !== null) {
                return this.isSpaceAdminOverride
            }
            return this.isSpaceAdmin
        },
        isSwitchCloudEnabled() {
            return false
        },
        mountPoints() {
            const points = this.mountedStorageList.map(mount => this.trimMountPath(mount.mount_path))
            if (this.selectedMount) return points.filter(point => point !== this.trimMountPath(this.selectedMount.mount_path))
            return points
        }
    },
    methods: {
        async fetchMountedStorageList() {
            this.fetchingMountedStorageList = true
            await new Promise(resolve => setTimeout(resolve, 350))
            this.fetchingMountedStorageList = false
        },
        onAttached(newMount) {
            const alreadyExists = this.mountedStorageList.some(mount => mount.mount_path === newMount.mount_path)
            if (alreadyExists) {
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'A storage is already mounted on that path.',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'error'
                })
                return
            }

            this.mountedStorageList = [{ ...newMount }, ...this.mountedStorageList]
            this.$store.dispatch('showSnackBar', {
                snackBarText: 'Cloud storage attached (demo mode).',
                snackBarTimeout: 4000,
                snackBarIcon: 'check_circle'
            })
        },
        sendPatchRequest() {
            this.editingMount = this.selectedMount.amid
            const mountPath = `/${this.mountPoint}`

            this.mountedStorageList = this.mountedStorageList.map(mount => {
                if (mount.amid === this.selectedMount.amid) {
                    return {
                        ...mount,
                        mount_path: mountPath
                    }
                }
                return mount
            })

            this.editingMount = null
            this.editMountDialog = false
            this.selectedMount = null
            this.$refs.mountEditForm.reset()

            this.$store.dispatch('showSnackBar', {
                snackBarText: 'Attached storage updated (demo mode).',
                snackBarTimeout: 4000,
                snackBarIcon: 'check_circle'
            })
        },
        openEditMountDialog(mount) {
            this.selectedMount = mount
            this.editMountDialog = true
        },
        openDeleteDialog(mount) {
            this.selectedMount = mount
            this.deleteMountDialog = true
        },
        confirmDeleteMount() {
            if (!this.selectedMount) {
                this.deleteMountDialog = false
                return
            }

            this.deletingMount = true
            this.mountedStorageList = this.mountedStorageList.filter(mount => mount.amid !== this.selectedMount.amid)
            this.deletingMount = false
            this.deleteMountDialog = false
            this.selectedMount = null

            this.$store.dispatch('showSnackBar', {
                snackBarText: 'Attached storage detached (demo mode).',
                snackBarTimeout: 4000,
                snackBarIcon: 'check_circle'
            })
        },
        trimMountPath(path) {
            return (path || '').replace(/^\//, '')
        }
    },
    watch: {
        spaceId() {
            this.fetchMountedStorageList()
        },
        selectedMount(to) {
            this.mountPoint = this.trimMountPath(to?.mount_path || '/')
        }
    },
    created() {
        this.fetchMountedStorageList()
    }
}
</script>

<style scoped>
.provider-logo {
    width: 20px;
    height: 20px;
    display: block;
}
</style>
